// ---------------------------------------------------------------------------
// Agent Catalog Registry
// Each agent defines its metadata, required inputs, outputs, and status.
//
// `id` MUST match the corresponding entry's `id` in the backend's
// _AGENT_CATALOG (ztd_app/api/modules/admin/v2_misc_router.py) -- that's
// what Project Setup's Agent Selection panel and ProjectAgent.agent_id
// are keyed on. The two catalogs drifted for years (data-masking vs
// data-guardian, data-model-creator vs semantic-intelligence, etc.) before
// being reconciled; adding a new agent here without the matching id there
// (or vice versa) reintroduces that split. AGENT_REQUIRED_PERMISSIONS in
// AgentCatalog.tsx is also keyed by this same id.
// ---------------------------------------------------------------------------

export type AgentStatus = "active" | "coming-soon" | "beta";

export interface AgentInputSpec {
  type:
    | "file-upload"
    | "db-connect"
    | "text-input"
    | "url-input"
    | "file-or-db";
  label: string;
  accept?: string[]; // file extensions for file-upload
  description: string;
}

export interface AgentOutputSpec {
  id: string;
  label: string;
  format: string; // yaml, sql, json, markdown, etc.
}

export interface AgentDefinition {
  id: string;
  name: string;
  shortName: string;
  description: string;
  longDescription: string;
  icon: string; // lucide icon name
  category: string;
  status: AgentStatus;
  llmEnabled: boolean; // true if agent uses LLM (API key required)
  inputs: AgentInputSpec[];
  outputs: AgentOutputSpec[];
  workflow: string[]; // ordered step labels
  color: string; // real hex color (e.g. "#86BC25") -- every agent uses the
  // same standardized brand accent; this is not a per-agent Tailwind token
}

// ---------------------------------------------------------------------------
// Agent Definitions
// ---------------------------------------------------------------------------

const STATUS_ORDER: Record<AgentStatus, number> = {
  active: 0,
  beta: 1,
  "coming-soon": 2,
};

export const AGENTS: AgentDefinition[] = (
  [
    // April 2026 — DQ Rules Engine merged into the Profiler agent. The
    // two surfaces had no independent runtime: every rule execution
    // already flowed through the profiler's session + run-history
    // pipeline, and every business-rule finding rendered in the
    // Profiler's Findings tab. Keeping them as separate "agents" in the
    // catalog created duplication without value. The merged agent below
    // (id: 'data-profiler') now advertises both capabilities.
    {
      id: "semantic-intelligence",
      name: "Semantic Intelligence",
      shortName: "Semantic Intelligence",
      description:
        "Upload datasets or connect to databases. AI profiles your data, asks smart questions, then generates a complete semantic model with deployment-ready DDL.",
      longDescription:
        "An intelligent Semantic Intelligence agent that takes raw data (CSV, Excel, JSON, Parquet, or live database connections) and builds a full semantic data model. It auto-profiles the dataset, engages in targeted Q&A to understand business context, then generates a semantic view with business glossary, ER diagrams, and deployment-ready DDL for Snowflake, Oracle, PostgreSQL, MySQL, SQL Server, or BigQuery. Supports Snowflake native Semantic Views (YAML) and Databricks Metric Views.",
      icon: "Database",
      category: "Data Engineering",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Dataset or Database",
          accept: [
            ".csv",
            ".tsv",
            ".txt",
            ".xlsx",
            ".xls",
            ".json",
            ".jsonl",
            ".parquet",
          ],
          description: "Upload a data file or connect to a relational database",
        },
      ],
      outputs: [
        { id: "profile", label: "Dataset Profile", format: "json" },
        { id: "semantic-view", label: "Semantic View", format: "yaml" },
        { id: "glossary", label: "Business Glossary", format: "markdown" },
        { id: "er-diagram", label: "ER Diagram", format: "mermaid" },
        { id: "target-ddl", label: "Target DDL", format: "sql" },
        {
          id: "snowflake-yaml",
          label: "Snowflake Semantic View",
          format: "yaml",
        },
        {
          id: "databricks-metric",
          label: "Databricks Metric View",
          format: "sql",
        },
        {
          id: "documentation",
          label: "Model Documentation",
          format: "docx/pdf/html",
        },
      ],
      workflow: [
        "Upload / Connect",
        "Auto-Profile",
        "Smart Q&A",
        "Semantic View",
        "Target Deploy",
        "Documentation",
      ],
      color: "#86BC25",
    },
    {
      id: "backlog-architect",
      name: "Scrum Planner",
      shortName: "Scrum Planner",
      description:
        "AI-powered epic & user story generation from business requirements using BMAD methodology. Upload BRDs, get sprint-ready backlogs with estimated story points.",
      longDescription:
        "An intelligent Agile backlog creation agent powered by the BMAD (BMad Agile Development) methodology. Upload Business Requirements Documents (Word, Excel, PDF) or paste requirements text. The AI extracts functional and non-functional requirements, asks targeted clarifying questions about ambiguities and technical implementation, then generates a complete backlog: Epics, User Stories with BDD acceptance criteria (Given/When/Then), task breakdowns, and Fibonacci story point estimates. Supports interactive review and editing, with export to formatted Excel, Word, JIRA (via API), or GitHub Issues.",
      icon: "BrainCircuit",
      category: "Agile Delivery",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "file-upload",
          label: "Business Requirements Document",
          accept: [".docx", ".xlsx", ".xls", ".pdf", ".txt", ".md"],
          description:
            "Upload a BRD, PRD, or requirements document (Word, Excel, PDF, or text)",
        },
        {
          type: "text-input",
          label: "Additional Context",
          description:
            "Paste additional requirements or provide context about the project, tech stack, and constraints",
        },
      ],
      outputs: [
        { id: "epics", label: "Epics", format: "json" },
        { id: "user-stories", label: "User Stories", format: "json" },
        {
          id: "acceptance-criteria",
          label: "Acceptance Criteria (BDD)",
          format: "markdown",
        },
        { id: "estimation", label: "Story Point Estimates", format: "json" },
        { id: "excel-export", label: "Excel Export", format: "xlsx" },
        { id: "word-export", label: "Word Export", format: "docx" },
        { id: "jira-export", label: "JIRA Export", format: "api" },
        { id: "github-issues", label: "GitHub Issues", format: "api" },
      ],
      workflow: [
        "Upload BRD",
        "AI Analysis & Clarification",
        "Epic Design",
        "Story Generation",
        "Review & Edit",
        "Export",
      ],
      color: "#86BC25",
    },
    {
      id: "test-planner",
      name: "QA Strategist",
      shortName: "QA Strategist",
      description:
        "Create comprehensive test strategies, generate test scenarios, test data, test cases, and executable test scripts from requirements or data models.",
      longDescription:
        "A comprehensive testing agent that generates: test strategies aligned with business risk, scenario-based test designs, synthetic test data, detailed test cases with expected results, and executable test scripts (Selenium, Cypress, Pytest). Works with requirements docs, user stories, or data models as input.",
      icon: "FlaskConical",
      category: "Quality Assurance",
      status: "coming-soon",
      llmEnabled: true,
      inputs: [
        {
          type: "file-upload",
          label: "Requirements / User Stories",
          accept: [".pdf", ".docx", ".txt", ".md", ".json", ".csv"],
          description: "Upload requirements, user stories, or data models",
        },
        {
          type: "text-input",
          label: "Application Context",
          description: "Describe the application and testing scope",
        },
      ],
      outputs: [
        { id: "test-strategy", label: "Test Strategy", format: "markdown" },
        { id: "test-scenarios", label: "Test Scenarios", format: "markdown" },
        { id: "test-data", label: "Synthetic Test Data", format: "csv/json" },
        { id: "test-cases", label: "Test Cases", format: "xlsx" },
        { id: "test-scripts", label: "Test Scripts", format: "python/js" },
      ],
      workflow: [
        "Input Context",
        "Risk Analysis",
        "Scenario Design",
        "Case Generation",
        "Script Generation",
        "Export",
      ],
      color: "#86BC25",
    },
    {
      // Story 1.6: id updated from "data-pipeline-creator" → "pipeline-engineer"
      // so the route /projects/:id/agents/pipeline-engineer resolves correctly.
      // status updated from "coming-soon" → "active" to show Launch Agent button.
      id: "pipeline-engineer",
      name: "Pipeline Engineer",
      shortName: "Pipeline Eng",
      description:
        "Configure source-to-target pipelines with pushdown SQL. Define mapping sheets, run full replace or incremental loads, and validate data quality rules — all without writing ETL code.",
      longDescription:
        "The Pipeline Engineer agent loads target tables from heterogeneous source databases using pushdown SQL — all data transformation runs inside the target database engine, not in Python. Create a named pipeline, connect source (MySQL, PostgreSQL, Oracle, Snowflake, Redshift, Databricks, Flat File) and target databases, then define mapping sheets: explicit column lists, SQL expressions, optional CTE steps, watermark-based incremental loads, and SCD1/SCD2 dimension management. AI-assisted schema introspection and job proposal accelerate mapping sheet creation. All generated SQL is stored as an audit artifact for every run.",
      icon: "GitBranch",
      category: "Data Engineering",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "db-connect",
          label: "Source Database",
          description:
            "Connect to MySQL, PostgreSQL, Oracle, Snowflake, Redshift, Databricks, or a flat file source",
        },
        {
          type: "db-connect",
          label: "Target Database",
          description:
            "Connect to Databricks, Snowflake, PostgreSQL, or any supported target",
        },
      ],
      outputs: [
        {
          id: "mapping-sheet",
          label: "Mapping Sheet",
          format: "json",
        },
        {
          id: "pushdown-sql",
          label: "Pushdown SQL",
          format: "sql",
        },
        {
          id: "dq-results",
          label: "DQ Rule Results",
          format: "json",
        },
        {
          id: "run-history",
          label: "Run History",
          format: "json",
        },
      ],
      workflow: [
        "Connect Sources",
        "Define Mappings",
        "Preview SQL",
        "Execute Load",
        "Validate DQ",
        "Run History",
      ],
      color: "#86BC25",
    },
    {
      id: "data-guardian",
      name: "Data Guardian",
      shortName: "Data Guardian",
      description:
        "Upload files or connect to databases. Select columns to mask with 11 masking techniques — redaction, hashing, shuffling, fake substitution, partial masking, and more.",
      longDescription:
        "A comprehensive data masking agent that protects sensitive information using 11 industry-standard techniques: Redaction, Randomization, Shuffling, Fake Substitution (names, emails, phones, addresses), Hashing (SHA-256/MD5), Partial Masking, Date Shifting, Nullification, Generalization/Bucketing, AES Encryption, and Format-Preserving masking. Auto-detects PII columns and suggests appropriate masking. Supports file uploads (CSV, Excel, JSON, Parquet) and live database connections. Target output can be downloaded as a file or written directly to a database with options to create, drop & recreate, or truncate & reload tables.",
      icon: "ShieldCheck",
      category: "Data Engineering",
      status: "active",
      llmEnabled: false,
      inputs: [
        {
          type: "file-or-db",
          label: "Dataset or Database",
          accept: [
            ".csv",
            ".tsv",
            ".txt",
            ".xlsx",
            ".xls",
            ".json",
            ".jsonl",
            ".parquet",
          ],
          description:
            "Upload a data file or connect to a relational database for masking",
        },
      ],
      outputs: [
        {
          id: "masked-dataset",
          label: "Masked Dataset",
          format: "csv/xlsx/parquet",
        },
        { id: "masking-audit", label: "Masking Audit Report", format: "json" },
        {
          id: "masking-config",
          label: "Masking Configuration",
          format: "json",
        },
      ],
      workflow: [
        "Upload / Connect",
        "Auto-Detect Sensitivity",
        "Configure Masking",
        "Preview",
        "Execute & Export",
      ],
      color: "#86BC25",
    },
    {
      id: "metadata-curator",
      name: "Metadata Curator",
      shortName: "Meta Curator",
      description:
        "Discover, generate, and govern column-level metadata from files, databases, or data dictionaries. AI-powered enrichment with human-in-the-loop approval and publish.",
      longDescription:
        "An intelligent metadata management agent that follows the BMAD (Business Metadata & Discovery) methodology. Upload data files (CSV, Excel, JSON, Parquet) or connect to enterprise databases (Snowflake, PostgreSQL, Oracle, SQL Server, MySQL, BigQuery, Redshift, Databricks). The agent auto-profiles your data, generates AI-enriched column-level metadata with descriptions, business glossary mappings, PII/sensitivity detection, and domain classification. It asks targeted clarification questions when uncertain, supports human review with confidence scores, and publishes clean, trusted metadata catalogs. Supports multi-table/multi-sheet discovery, version history, and write-back to source databases.",
      icon: "BookMarked",
      category: "Data Governance",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Dataset, Database, or Data Dictionary",
          accept: [
            ".csv",
            ".tsv",
            ".txt",
            ".xlsx",
            ".xls",
            ".json",
            ".jsonl",
            ".parquet",
          ],
          description:
            "Upload a data file or connect to a database for metadata discovery",
        },
        {
          type: "file-upload",
          label: "Supporting Documentation",
          accept: [".pdf", ".docx", ".xlsx", ".txt", ".md"],
          description:
            "Upload BRDs, data dictionaries, or existing glossaries (optional)",
        },
      ],
      outputs: [
        {
          id: "column-metadata",
          label: "Column Metadata Catalog",
          format: "json",
        },
        {
          id: "glossary-mapping",
          label: "Business Glossary",
          format: "markdown",
        },
        {
          id: "sensitivity-report",
          label: "Sensitivity & PII Report",
          format: "json",
        },
        { id: "lineage-map", label: "Data Lineage Map", format: "mermaid" },
        {
          id: "catalog-export",
          label: "Catalog Export",
          format: "csv/json/yaml",
        },
      ],
      workflow: [
        "Upload / Connect",
        "Auto-Profile",
        "AI Enrichment",
        "Clarify & Review",
        "Publish & Export",
      ],
      color: "#86BC25",
    },
    {
      id: "validation",
      name: "Data Validator",
      shortName: "Data Validator",
      description:
        "Run schema tests and data contract validation against source or target datasets. Catch structural, referential, and business-rule violations before they reach production.",
      longDescription:
        "A contract-first validation agent that enforces schema expectations and data contracts on files or live database connections. Validates column types, nullable constraints, primary and foreign key integrity, domain value ranges, referential consistency, and custom SQL-based business rules. Generates a structured test report with pass/fail counts, failing record samples (metadata-only, no raw cell values), and a remediation plan. Integrates with Great Expectations and dbt test suites for downstream pipeline enforcement.",
      icon: "ClipboardCheck",
      category: "Data Quality",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Dataset or Database",
          accept: [".csv", ".tsv", ".xlsx", ".json", ".parquet"],
          description:
            "Upload a data file or connect to a relational database for validation",
        },
        {
          type: "file-upload",
          label: "Data Contract / Expectation Suite",
          accept: [".yaml", ".yml", ".json"],
          description:
            "Optional: upload an existing contract or Great Expectations suite",
        },
      ],
      outputs: [
        { id: "validation-report", label: "Validation Report", format: "json" },
        { id: "schema-tests", label: "Schema Tests", format: "sql/yaml" },
        { id: "ge-suite", label: "Great Expectations Suite", format: "json" },
        { id: "dbt-tests", label: "dbt Test YAML", format: "yaml" },
        { id: "remediation", label: "Remediation Plan", format: "markdown" },
      ],
      workflow: [
        "Upload / Connect",
        "Contract Definition",
        "Rule Compilation",
        "Execute Tests",
        "Review Findings",
        "Export",
      ],
      color: "#86BC25",
    },
    {
      id: "data-converter",
      name: "Data Converter",
      shortName: "Data Converter",
      description:
        "Translate DDL and data models across database platforms. Convert Oracle to Snowflake, SQL Server to BigQuery, or any major RDBMS — with type mapping, constraint preservation, and migration scripts.",
      longDescription:
        "A cross-database migration agent that handles DDL translation across all major relational platforms: Oracle, SQL Server, PostgreSQL, MySQL, Snowflake, BigQuery, Redshift, Databricks, and Synapse. Parses source DDL or introspects live schemas, applies type-mapping rules, preserves constraints and indexes, and generates target-platform DDL with optional data-migration scripts. Flags incompatible constructs (stored procedures, proprietary functions) and suggests cloud-native equivalents. Outputs a compatibility report alongside migration-ready SQL.",
      icon: "ArrowLeftRight",
      category: "Data Engineering",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Source Schema or DDL",
          accept: [".sql", ".ddl", ".txt"],
          description:
            "Upload source DDL script or connect to the source database",
        },
      ],
      outputs: [
        { id: "target-ddl", label: "Target DDL", format: "sql" },
        {
          id: "type-mapping",
          label: "Type Mapping Report",
          format: "markdown",
        },
        {
          id: "compatibility-report",
          label: "Compatibility Report",
          format: "json",
        },
        { id: "migration-script", label: "Migration Script", format: "sql" },
      ],
      workflow: [
        "Load Source Schema",
        "Parse & Analyze",
        "Type Mapping",
        "DDL Generation",
        "Compatibility Review",
        "Export",
      ],
      color: "#86BC25",
    },
    {
      id: "conversational-ai",
      name: "Conversational AI",
      shortName: "Conversational AI",
      description:
        "Query your project's connected database in plain English. Ask business questions and get back a grounded answer with the query behind it — no SQL required.",
      longDescription:
        "A natural language interface for data exploration, scoped to a project's already-connected source database (Phase 1). Ask a business question in plain English; the agent resolves it to a structured query, validates it against the schema and this codebase's guardrail stack (joins limited to declared foreign keys, row-level access policies, no raw cell values ever sent to the LLM), executes it, and returns a narrative answer grounded only in that result. Maintains conversation context across turns and logs every question, resolved query, and answer for audit compliance. Chart-style visualization and canned/proactive insights land in a later phase.",
      icon: "MessageSquare",
      category: "Data Access",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "text-input",
          label: "Natural Language Question",
          description:
            "Ask any business question about this project's connected database",
        },
      ],
      outputs: [
        { id: "sql-query", label: "Resolved Query", format: "sql" },
        { id: "result-table", label: "Query Results", format: "json" },
        { id: "narrative", label: "Narrative Explanation", format: "markdown" },
        { id: "query-history", label: "Query History", format: "json" },
      ],
      workflow: [
        "Ask Question",
        "Query Resolution",
        "Execute & Review",
        "Explore Further",
      ],
      color: "#86BC25",
    },
    {
      id: "mdm-agent",
      name: "Master Data Management",
      shortName: "MDM",
      description:
        "Resolve duplicate entities and build golden records across source systems. AI-powered matching with configurable survivorship rules and master data publishing.",
      longDescription:
        "A Master Data Management agent that identifies, scores, and merges duplicate entity records across source systems. Applies probabilistic and deterministic matching algorithms for customers, vendors, employees, and products. Generates match clusters with confidence scores, presents candidate merges for human review, and applies configurable survivorship rules to produce authoritative golden records. Publishes master records to target systems or a central MDM hub.",
      icon: "Users",
      category: "Data Governance",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Source Entity Data",
          accept: [".csv", ".xlsx", ".json", ".parquet"],
          description:
            "Upload entity records or connect to source systems for deduplication",
        },
      ],
      outputs: [
        { id: "match-clusters", label: "Match Clusters", format: "json" },
        { id: "golden-records", label: "Golden Records", format: "csv/json" },
        {
          id: "survivorship-config",
          label: "Survivorship Rules",
          format: "yaml",
        },
        { id: "mdm-report", label: "MDM Report", format: "markdown" },
      ],
      workflow: [
        "Load Source Data",
        "Profile & Standardize",
        "Match & Cluster",
        "Human Review",
        "Survivorship",
        "Publish",
      ],
      color: "#86BC25",
    },
    {
      id: "compliance",
      name: "Compliance",
      shortName: "Compliance",
      description:
        "Enforce FedRAMP, HIPAA, SOX, and GDPR data policies across your datasets. Automated compliance scans with audit-ready evidence packages.",
      longDescription:
        "A policy enforcement agent that scans datasets and pipelines for compliance violations across major regulatory frameworks: FedRAMP, HIPAA, SOX, GDPR, CCPA, and FISMA. Detects unprotected PII/PHI, missing audit trails, improper retention schedules, and access-control gaps. Generates audit-ready evidence packages with control mappings, finding summaries, and remediation playbooks aligned to each framework's control catalog.",
      icon: "Scale",
      category: "Data Governance",
      status: "coming-soon",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Dataset or Schema",
          accept: [".csv", ".xlsx", ".json", ".sql"],
          description:
            "Upload data or connect to a database for compliance scanning",
        },
        {
          type: "file-upload",
          label: "Policy Configuration",
          accept: [".yaml", ".json"],
          description:
            "Optional: upload custom compliance policy configuration",
        },
      ],
      outputs: [
        { id: "compliance-report", label: "Compliance Report", format: "pdf" },
        { id: "control-mapping", label: "Control Mapping", format: "xlsx" },
        { id: "findings", label: "Findings Register", format: "json" },
        {
          id: "remediation",
          label: "Remediation Playbook",
          format: "markdown",
        },
      ],
      workflow: [
        "Select Frameworks",
        "Scan Dataset",
        "Detect Violations",
        "Map to Controls",
        "Human Review",
        "Export Evidence",
      ],
      color: "#86BC25",
    },
    {
      id: "dataops",
      name: "DataOps",
      shortName: "DataOps",
      description:
        "Monitor data pipelines, track SLA health, and generate automated alerts when pipeline runs fail or data quality degrades.",
      longDescription:
        "A pipeline observability agent that monitors data pipeline runs, enforces SLA thresholds, and surfaces anomalies in pipeline behavior. Integrates with Airflow, dbt, Glue, and Azure Data Factory to collect run metadata, latency metrics, and row-count trends. Sends alerts via Slack, email, or PagerDuty when SLAs breach, jobs fail, or data quality scores drop. Generates incident summaries and root-cause analyses from pipeline logs.",
      icon: "Activity",
      category: "DataOps",
      status: "coming-soon",
      llmEnabled: true,
      inputs: [
        {
          type: "url-input",
          label: "Pipeline Endpoint / API",
          description:
            "Connect to your orchestration platform (Airflow, dbt Cloud, etc.)",
        },
      ],
      outputs: [
        { id: "sla-dashboard", label: "SLA Dashboard", format: "html" },
        { id: "incident-report", label: "Incident Report", format: "markdown" },
        { id: "alert-config", label: "Alert Configuration", format: "yaml" },
        {
          id: "root-cause",
          label: "Root Cause Analysis",
          format: "markdown",
        },
      ],
      workflow: [
        "Connect Orchestrator",
        "Ingest Run Metadata",
        "SLA Evaluation",
        "Anomaly Detection",
        "Alert & Notify",
        "Incident Summary",
      ],
      color: "#86BC25",
    },
    {
      id: "db-health",
      name: "DB Health",
      shortName: "DB Health",
      description:
        "Diagnose database performance issues, identify slow queries, and generate targeted remediation recommendations — index additions, query rewrites, and configuration tuning.",
      longDescription:
        "A database performance agent that connects to relational databases and analyses query execution plans, index usage, table statistics, lock contention, and configuration parameters. Identifies the top performance bottlenecks — missing indexes, table scans, bloated statistics, suboptimal join orders — and generates prioritised remediation recommendations with expected impact estimates. Supports PostgreSQL, Oracle, SQL Server, MySQL, and Snowflake.",
      icon: "HeartPulse",
      category: "DataOps",
      status: "coming-soon",
      llmEnabled: true,
      inputs: [
        {
          type: "db-connect",
          label: "Database Connection",
          description: "Connect to the target database for health analysis",
        },
      ],
      outputs: [
        { id: "health-report", label: "Health Report", format: "markdown" },
        {
          id: "slow-queries",
          label: "Slow Query Analysis",
          format: "json",
        },
        {
          id: "index-recommendations",
          label: "Index Recommendations",
          format: "sql",
        },
        {
          id: "config-tuning",
          label: "Config Tuning Guide",
          format: "markdown",
        },
      ],
      workflow: [
        "Connect Database",
        "Collect Diagnostics",
        "Analyse Bottlenecks",
        "Generate Recommendations",
        "Review & Apply",
      ],
      color: "#86BC25",
    },
    {
      id: "data-lineage",
      name: "Data Lineage",
      shortName: "Data Lineage",
      description:
        "Visualise end-to-end data lineage from source to consumption. Trace column-level impact, assess downstream blast radius, and document data provenance.",
      longDescription:
        "A lineage discovery agent that traces the end-to-end journey of data from ingestion sources through transformations to consumption layers. Parses SQL scripts, dbt models, Airflow DAGs, and Spark jobs to build a directed acyclic graph of column-level dependencies. Enables impact analysis — given a source column change, which downstream reports and models are affected? Integrates with OpenLineage and Marquez for standards-compliant lineage emission.",
      icon: "GitMerge",
      category: "Data Governance",
      status: "coming-soon",
      llmEnabled: true,
      inputs: [
        {
          type: "file-upload",
          label: "SQL Scripts / dbt Models",
          accept: [".sql", ".yml", ".yaml", ".py"],
          description:
            "Upload transformation scripts, dbt models, or pipeline definitions",
        },
        {
          type: "db-connect",
          label: "Source / Target Database",
          description:
            "Connect to databases for schema-level lineage discovery",
        },
      ],
      outputs: [
        { id: "lineage-graph", label: "Lineage Graph", format: "json/html" },
        { id: "impact-analysis", label: "Impact Analysis", format: "markdown" },
        {
          id: "column-lineage",
          label: "Column-Level Lineage",
          format: "json",
        },
        {
          id: "openlineage-export",
          label: "OpenLineage Export",
          format: "json",
        },
      ],
      workflow: [
        "Load Artifacts",
        "Parse Dependencies",
        "Build Lineage Graph",
        "Impact Analysis",
        "Publish",
      ],
      color: "#86BC25",
    },
    {
      id: "data-observability",
      name: "Data Observability",
      shortName: "Observability",
      description:
        "Continuously monitor data freshness, volume, schema drift, and distribution anomalies. Get notified before bad data reaches dashboards or ML models.",
      longDescription:
        "A data observability agent that instruments datasets and pipelines with automated monitors for the five pillars of data health: freshness, volume, schema, distribution, and lineage. Uses statistical baselines and ML-based anomaly detection to surface unexpected changes — late-arriving tables, row-count drops, column type changes, and distribution shifts. Integrates with Monte Carlo, Great Expectations, and Soda Core data contracts. Sends alerts with root-cause context before bad data reaches consumers.",
      icon: "Binoculars",
      category: "DataOps",
      status: "coming-soon",
      llmEnabled: true,
      inputs: [
        {
          type: "db-connect",
          label: "Data Source",
          description: "Connect to the database or data warehouse to monitor",
        },
      ],
      outputs: [
        { id: "monitors", label: "Monitor Configuration", format: "yaml" },
        { id: "anomaly-report", label: "Anomaly Report", format: "json" },
        {
          id: "freshness-dashboard",
          label: "Freshness Dashboard",
          format: "html",
        },
        { id: "alert-rules", label: "Alert Rules", format: "yaml" },
      ],
      workflow: [
        "Connect Sources",
        "Define Baselines",
        "Deploy Monitors",
        "Detect Anomalies",
        "Alert & Report",
      ],
      color: "#86BC25",
    },
    {
      id: "test-data-management",
      name: "Test Data Manager",
      shortName: "Test Data Mgr",
      description:
        "Provision realistic synthetic test datasets, mask production data for non-prod environments, and manage test environment lifecycle end-to-end.",
      longDescription:
        "A test data management agent that generates statistically realistic synthetic datasets from schema definitions or production samples, with full referential integrity and business-rule compliance. Masks production data for safe use in lower environments using the same 11 techniques as Data Guardian. Manages test environment provisioning — creating, refreshing, and retiring dataset snapshots on demand. Integrates with CI/CD pipelines for automated test data refresh before each sprint.",
      icon: "TestTube2",
      category: "Quality Assurance",
      status: "coming-soon",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Schema or Production Sample",
          accept: [".sql", ".csv", ".json", ".xlsx"],
          description:
            "Provide schema DDL or a production data sample to base synthetic data on",
        },
        {
          type: "text-input",
          label: "Volume & Constraint Spec",
          description:
            "Describe row counts, distributions, and business constraints for generated data",
        },
      ],
      outputs: [
        { id: "synthetic-data", label: "Synthetic Dataset", format: "csv/sql" },
        {
          id: "masking-config",
          label: "Masking Configuration",
          format: "json",
        },
        {
          id: "env-snapshot",
          label: "Environment Snapshot",
          format: "sql/parquet",
        },
        { id: "data-spec", label: "Data Generation Spec", format: "yaml" },
      ],
      workflow: [
        "Load Schema",
        "Define Constraints",
        "Generate Data",
        "Validate Integrity",
        "Provision Environment",
        "Export",
      ],
      color: "#86BC25",
    },
    {
      id: "data-profiler",
      name: "Data Profiler & Quality Rules Engine",
      shortName: "Data Profiler",
      description:
        "Production-grade data profiling AND business-rule authoring/execution in one workflow: statistical analysis, PK/FK + relationship discovery, DQ findings, plain-English business rules, NSLDS-seeded rule library, and Great Expectations suite generation.",
      longDescription:
        "A unified profiling + quality-rules agent. Performs deep statistical analysis on files or live database connections (schema profiling with pattern detection — email, phone, SSN, UUID, etc.; primary and foreign key candidate detection; cross-table relationship discovery via value overlap and name matching; data quality issue detection across 7 categories: uniqueness, completeness, validity, consistency, referential, timeliness, accuracy). On the same runtime it lets you author business rules — in plain English or via the structured form — covering 7 rule types (referential integrity, range/domain, conditional IF/THEN, temporal consistency, cross-table aggregate, format pattern, statistical distribution). Rules compile to parameterized SQL (or DuckDB/pandas for files) and return pass/fail counts plus metadata — never raw cell values. Ships with a pre-seeded NSLDS rule library covering the top 11 FSA DQ priorities. Generates a Great Expectations suite for downstream pipelines, builds an LLM-driven business interpretation, persists every run for history/compare, and exports failing-rule findings to the Scrum Planner for remediation story generation.",
      icon: "BarChart3",
      category: "Data Engineering",
      status: "active",
      llmEnabled: true,
      inputs: [
        {
          type: "file-or-db",
          label: "Dataset or Database",
          accept: [
            ".csv",
            ".tsv",
            ".txt",
            ".xlsx",
            ".xls",
            ".json",
            ".jsonl",
            ".parquet",
          ],
          description:
            "Upload a data file or connect to a relational database for profiling and rule execution",
        },
        {
          type: "text-input",
          label: "Rule description (optional)",
          description:
            "Describe a business rule in plain English; the LLM parses it into a canonical IR with confidence scores for review before save.",
        },
      ],
      outputs: [
        { id: "profile", label: "Statistical Profile", format: "json" },
        { id: "dq-report", label: "Data Quality Report", format: "json" },
        { id: "pk-candidates", label: "Key Candidates", format: "json" },
        {
          id: "relationships",
          label: "Relationship Map",
          format: "json/mermaid",
        },
        { id: "rule-library", label: "Rule Library", format: "json" },
        {
          id: "execution-results",
          label: "Rule Execution Results",
          format: "json",
        },
        {
          id: "sample-failures",
          label: "Sample Failures (metadata-only)",
          format: "json",
        },
        { id: "ge-suite", label: "Great Expectations Suite", format: "json" },
        {
          id: "history",
          label: "Run History (per-run snapshots)",
          format: "json",
        },
        { id: "remediation", label: "Remediation Plan", format: "json" },
        {
          id: "interpretation",
          label: "AI Interpretation",
          format: "markdown",
        },
        { id: "run-config", label: "Run Configuration", format: "json" },
      ],
      workflow: [
        "Upload / Connect",
        "Configure & Run",
        "Profile & DQ",
        "Author / Execute Rules",
        "AI Interpret",
        "Review & Export",
      ],
      color: "#86BC25",
    },
  ] as AgentDefinition[]
).sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getAgent(id: string): AgentDefinition | undefined {
  return AGENTS.find((a) => a.id === id);
}

export function getActiveAgents(): AgentDefinition[] {
  return AGENTS.filter((a) => a.status === "active");
}

export function getAgentsByCategory(): Record<string, AgentDefinition[]> {
  return AGENTS.reduce(
    (acc, agent) => {
      if (!acc[agent.category]) acc[agent.category] = [];
      acc[agent.category].push(agent);
      return acc;
    },
    {} as Record<string, AgentDefinition[]>,
  );
}
